import { Badge, Grid } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
// import { ReactComponent as HamburgerIcon } from "../../../assets/images/header/hamburger.svg";
import { logout } from "../../../apis/utils";
import CustomDrawer from "../../../components/CustomDrawer";
import { P1 } from "../../../components/NormalFonts";
import { useAuth } from "../../../context/AuthContext";
import { useSignOut } from "../../../hooks/Authentication/auth.hooks";
import { useGetCartStatus } from "../../../hooks/Cart/cart.hooks";
import { useGetMessageUnread } from "../../../hooks/MessageUnread/messageunread.hooks";
import { HeaderContainer } from "./components";
import { MenuType } from "./data";
import styles from "./style.module.scss";

type CustomMenuType = {};

const ABOUT_PATH = "/news/detail/9";
const PRICE_PATH = "/news/detail/12";
const COURSE_INTRO_PATH = "/news/detail/7";

const HeaderComponent = () => {
  const { useBreakpoint } = Grid;
  const {
    isAuthenticated,
    setAuthModalVisible,
    setLoginForm,
    setRegisterForm,
  } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { mutateAsync: signout } = useSignOut();
  const [showMenuDrawer, setShowMenuDrawer] = useState<boolean>(false);
  const [showProfileDrawer, setShowProfileDrawer] = useState<boolean>(false);

  const { data: messageUnread } = useGetMessageUnread({
    enabled: isAuthenticated,
  });

  const { data: cartStatus, refetch: refetchCartStatus } = useGetCartStatus({
    enabled: isAuthenticated,
  });

  const MENU: MenuType[] = [
    {
      key: "home",
      label: t("header.menu.home"),
      path: "/",
      type: "item",
      visible: true,
    },
    {
      key: "coaches",
      label: t([
        `header.menu.book.${process.env.REACT_APP_PROD_NAME_DEV}`,
        "header.menu.book",
      ]),
      path: "/trainers",
      extraPath: "/trainers/info/",
      type: "item",
      visible: true,
    },
    {
      key: "guide",
      label: t("header.menu.guide"),
      path: "/guide",
      type: "item",
      visible: false,
    },
    {
      key: "about",
      label: t("header.menu.about"),
      path: ABOUT_PATH,
      type: "item",
      visible: true && process.env.REACT_APP_PROD_NAME_DEV === "jstyleski",
    },
    {
      key: "price",
      label: t("header.menu.price"),
      path: PRICE_PATH,
      type: "item",
      visible: true && process.env.REACT_APP_PROD_NAME_DEV === "jstyleski",
    },
    {
      key: "course_intro",
      label: t("header.menu.courseIntro"),
      path: COURSE_INTRO_PATH,
      type: "item",
      visible: true && process.env.REACT_APP_PROD_NAME_DEV === "jstyleski",
    },
    {
      key: "news",
      label: t([
        `header.menu.news.${process.env.REACT_APP_PROD_NAME_DEV}`,
        "header.menu.news",
      ]),
      path: "/news",
      extraPath: "/news/detail/",
      ignorePath: [ABOUT_PATH, PRICE_PATH, COURSE_INTRO_PATH],
      type: "item",
      visible: true,
    },
    {
      key: "faq",
      label: t([
        `header.menu.faq.${process.env.REACT_APP_PROD_NAME_DEV}`,
        "header.menu.faq",
      ]),
      path: "/faq",
      type: "item",
      visible: true,
    },
    {
      key: "record",
      label: t("header.menu.record"),
      type: "item",
      path: "/record",
      visible: isAuthenticated && !screens.md,
      dot: screens.md ? false : messageUnread?.has_unread_message || false,
    },
    {
      key: "profile",
      label: t("header.menu.personal"),
      path: "/profile",
      type: "item",
      visible: isAuthenticated && !screens.md,
    },
    {
      key: "signout",
      label: t("normal.logout"),
      path: "/signout",
      type: "auth",
      visible: isAuthenticated && !screens.md,
      onClick: async () => {
        try {
          await signout();
          setTimeout(() => {
            logout();
          }, 500);
        } catch (error: any) {}
      },
    },
    {
      key: "signin",
      label: t("normal.login"),
      path: "/",
      type: "auth",
      visible: isAuthenticated === false && !screens.md,
      onClick: () => {
        setAuthModalVisible(true);
        setLoginForm();
      },
    },
    {
      key: "signup",
      label: t("normal.register"),
      path: "/",
      type: "auth",
      visible: isAuthenticated === false && !screens.md,
      onClick: () => {
        setAuthModalVisible(true);
        setRegisterForm();
      },
    },
  ];

  const PROFILE: MenuType[] = [
    {
      key: "record",
      label: t("header.menu.record"),
      type: "item",
      path: "/record",
      visible: isAuthenticated,
      dot: messageUnread?.has_unread_message || false,
    },
    {
      key: "profile",
      label: t("header.menu.personal"),
      path: "/profile",
      type: "item",
      visible: isAuthenticated,
    },
    {
      key: "signout",
      label: t("normal.logout"),
      path: "/signout",
      type: "item",
      visible: isAuthenticated,
      onClick: async () => {
        await signout();
        setTimeout(() => {
          logout();
        }, 500);
      },
    },
    {
      key: "signin",
      label: t("normal.login"),
      path: "/",
      type: "item",
      visible: isAuthenticated === false,
      onClick: () => {
        setLoginForm();
      },
    },
    {
      key: "signup",
      label: t("normal.register"),
      path: "/",
      type: "item",
      visible: isAuthenticated === false,
      onClick: () => {
        setRegisterForm();
      },
    },
  ];

  const onMenuClose = () => {
    setShowMenuDrawer(false);
  };

  const onMenuOpen = () => {
    setShowMenuDrawer(true);
  };

  const onMenuProfileClose = () => {
    setShowProfileDrawer(false);
  };

  const onMenuProfileOpen = () => {
    setShowProfileDrawer(true);
  };

  const handleLogoClick = () => {
    navigate("/", { replace: true });
    const element = document.getElementById("body-container");
    if (element) {
      element.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLoginClick = () => {
    setLoginForm();
    setAuthModalVisible(true);
  };

  const renderDrawerMenu = () => {
    return MENU.filter(
      (_menu: MenuType & CustomMenuType) => _menu.visible === true
    ).map((_menu: MenuType) => {
      return (
        <Link
          onClick={(e) => {
            if (_menu.onClick) {
              e.preventDefault();
              _menu.onClick();
            }
            setShowMenuDrawer(false);
          }}
          to={_menu.path}
          key={_menu.key}
        >
          <Badge dot={_menu.dot}>
            <P1 color={_menu.type === "item" ? "#1C222B" : "var(--primary)"}>
              {_menu.label}
            </P1>
          </Badge>
        </Link>
      );
    });
  };

  const renderProfileMenu = () => {
    return PROFILE.filter(
      (_menu: MenuType & CustomMenuType) => _menu.visible === true
    ).map((_menu: MenuType) => {
      return (
        <Link
          onClick={() => {
            setShowMenuDrawer(false);
          }}
          to={_menu.path}
          key={_menu.key}
        >
          <P1 color="#1C222B">{_menu.label}</P1>
        </Link>
      );
    });
  };

  useEffect(() => {
    if (!cartStatus) return;
    console.debug("cartStatus", cartStatus);
  }, [cartStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const intervalCartStatus = setInterval(() => {
      refetchCartStatus();
    }, 1000 * 60);

    return () => clearInterval(intervalCartStatus);
  }, [isAuthenticated]);

  return (
    <>
      <HeaderContainer
        handleLogoClick={handleLogoClick}
        onMenuOpen={onMenuOpen}
        messageUnread={messageUnread}
        MENU={MENU}
        PROFILE={PROFILE}
        cartStatus={cartStatus}
        handleLoginClick={handleLoginClick}
      />
      <CustomDrawer
        placement={"right"}
        closable={true}
        onClose={onMenuClose}
        open={showMenuDrawer}
        width={"100vw"}
        size={"large"}
      >
        <div className={styles.drawerBodyContainer}>{renderDrawerMenu()}</div>
      </CustomDrawer>

      <CustomDrawer
        placement={"right"}
        closable={true}
        onClose={onMenuProfileClose}
        open={showProfileDrawer}
        width={"100vw"}
        size={"large"}
      >
        <div className={styles.drawerBodyContainer}>{renderProfileMenu()}</div>
      </CustomDrawer>
    </>
  );
};

export default HeaderComponent;
